import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { postService } from '../services/postService';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { formatShortDate, getPostTypeLabel, POST_TYPES, truncateText } from '../utils/formatters';

export default function PostsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const postTypeFilter = searchParams.get('type') || '';

  useEffect(() => {
    fetchPosts();
  }, [postTypeFilter]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await postService.getAll(postTypeFilter);
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await postService.delete(deleteId);
      setPosts(posts.filter((p) => p.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting post:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    if (value) {
      setSearchParams({ type: value });
    } else {
      setSearchParams({});
    }
  };

  const columns = [
    {
      key: 'image_url',
      label: 'Фото',
      render: (value) => (
        <img
          src={value || 'https://via.placeholder.com/50'}
          alt="Post"
          className="h-12 w-12 rounded object-cover"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/50';
          }}
        />
      ),
    },
    {
      key: 'title',
      label: 'Заголовок',
      render: (value) => <span className="font-medium">{truncateText(value, 50)}</span>,
    },
    {
      key: 'post_type',
      label: 'Тип',
      render: (value) => {
        const type = typeof value === 'number'
          ? ['', 'NEWS', 'PROJECT', 'PARTNER'][value]
          : value;
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
            {getPostTypeLabel(type)}
          </span>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Дата',
      render: (value) => formatShortDate(value),
    },
  ];

  const postTypeOptions = Object.values(POST_TYPES);

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Посты</h1>
        <Link to="/admin/posts/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Создать пост
          </Button>
        </Link>
      </div>

      <div className="mb-6">
        <Select
          value={postTypeFilter}
          onChange={handleFilterChange}
          options={postTypeOptions}
          placeholder="Все типы"
          className="w-48"
        />
      </div>

      <Table
        columns={columns}
        data={posts}
        loading={loading}
        actions={(row) => (
          <div className="flex items-center justify-end gap-2">
            <a
              href={`https://zhasylzhelken.kz/news/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-600"
              title="Просмотр"
            >
              <Eye className="h-5 w-5" />
            </a>
            <Link
              to={`/admin/posts/${row.id}/edit`}
              className="text-primary-600 hover:text-primary-800"
              title="Редактировать"
            >
              <Edit className="h-5 w-5" />
            </Link>
            <button
              onClick={() => setDeleteId(row.id)}
              className="text-red-500 hover:text-red-700"
              title="Удалить"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        )}
      />

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Удаление поста"
        message="Вы уверены, что хотите удалить этот пост? Это действие нельзя отменить."
        confirmText="Удалить"
        loading={deleting}
      />
    </div>
  );
}
